// The station's HTTP client for the ENP API: device pairing and tokens, device config,
// entitlement keys, and `POST /commands`. Every answer is parsed with the shared contract in
// @enp/domain (CONVENTIONS §5), so a server/station mismatch fails loudly, never silently.

import {
  type CommandBatchResponse,
  type DeviceConfigResponse,
  type DeviceRegisterRequest,
  type DeviceRegisterResponse,
  type DeviceTokenRequest,
  type DeviceTokenResponse,
  commandBatchResponseSchema,
  deviceConfigResponseSchema,
  deviceRegisterResponseSchema,
  deviceTokenResponseSchema,
} from '@enp/domain';
import { errorMessage } from './util.js';

/** A failed API call: no answer at all (`network`), or an HTTP error status. */
export class ApiError extends Error {
  constructor(
    readonly kind: 'network' | 'http' | 'contract',
    message: string,
    readonly status?: number,
    /** The problem+json `code`, when there is one. */
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** No answer: offline, DNS, refused, timed out. */
  get isNetwork(): boolean {
    return this.kind === 'network';
  }
}

/** One Ed25519 public key as `/entitlements/keys` lists it (JWK). */
export interface EntitlementJwk {
  kid: string;
  kty: string;
  crv?: string;
  x?: string;
}

export interface ApiClientOptions {
  /** e.g. `http://192.168.1.10:4000` (no trailing slash needed). */
  baseUrl: string;
  fetch?: typeof fetch;
  /** Per-request timeout. Default 20 s. */
  timeoutMs?: number;
}

export class ApiClient {
  readonly baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #timeoutMs: number;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.#fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.#timeoutMs = options.timeoutMs ?? 20_000;
  }

  /** `POST /devices/register` (unauthenticated: the access key is the credential). */
  async register(body: DeviceRegisterRequest): Promise<DeviceRegisterResponse> {
    const json = await this.#request('POST', '/devices/register', { body });
    return parse(deviceRegisterResponseSchema, json, '/devices/register');
  }

  /** `POST /devices/token`: the refresh secret → a 15-minute device JWT. */
  async token(body: DeviceTokenRequest): Promise<DeviceTokenResponse> {
    const json = await this.#request('POST', '/devices/token', { body });
    return parse(deviceTokenResponseSchema, json, '/devices/token');
  }

  /** `GET /devices/config`, with the local instants just before sending and on arrival. */
  async config(
    token: string,
    now: () => Date,
  ): Promise<{ config: DeviceConfigResponse; sentAt: Date; receivedAt: Date }> {
    const sentAt = now();
    const json = await this.#request('GET', '/devices/config', { token });
    const receivedAt = now();
    return {
      config: parse(deviceConfigResponseSchema, json, '/devices/config'),
      sentAt,
      receivedAt,
    };
  }

  /** `GET /entitlements/keys`: the public keys entitlement tokens are signed with. */
  async entitlementKeys(): Promise<EntitlementJwk[]> {
    const json = (await this.#request('GET', '/entitlements/keys', {})) as { keys?: unknown };
    if (!Array.isArray(json?.keys)) {
      throw new ApiError('contract', '/entitlements/keys: unexpected answer', 200);
    }
    return json.keys.filter(
      (k): k is EntitlementJwk =>
        typeof k === 'object' && k !== null && typeof (k as EntitlementJwk).kid === 'string',
    );
  }

  /** `POST /commands`: one answer per command, in order. */
  async postCommands(token: string, commands: unknown[]): Promise<CommandBatchResponse> {
    const json = await this.#request('POST', '/commands', { token, body: { commands } });
    return parse(commandBatchResponseSchema, json, '/commands');
  }

  async #request(
    method: 'GET' | 'POST',
    path: string,
    { token, body }: { token?: string; body?: unknown },
  ): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers['authorization'] = `Bearer ${token}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.#timeoutMs);
    let res: Response;
    try {
      res = await this.#fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        cache: 'no-store',
      });
    } catch (err) {
      throw new ApiError('network', `${path}: no answer (${errorMessage(err)})`);
    } finally {
      clearTimeout(timer);
    }
    const text = await res.text().catch(() => '');
    let json: unknown = undefined;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = text;
      }
    }
    if (!res.ok) {
      const problem = (typeof json === 'object' && json !== null ? json : {}) as {
        code?: unknown;
        title?: unknown;
        detail?: unknown;
      };
      const code = typeof problem.code === 'string' ? problem.code : undefined;
      const title =
        typeof problem.detail === 'string'
          ? problem.detail
          : typeof problem.title === 'string'
            ? problem.title
            : res.statusText;
      throw new ApiError('http', `${path}: HTTP ${res.status} ${title}`, res.status, code, json);
    }
    return json;
  }
}

function parse<T>(
  schema: { safeParse(v: unknown): { success: true; data: T } | { success: false; error: Error } },
  json: unknown,
  path: string,
): T {
  const result = schema.safeParse(json);
  if (!result.success) {
    throw new ApiError(
      'contract',
      `${path}: answer does not match the contract`,
      200,
      undefined,
      json,
    );
  }
  return result.data;
}
